/**
 * Client-side media upload validation aligned with API billing rules
 * (photo 25 MB / video 500 MB / audio 100 MB; video/audio paid-only).
 */

import i18n from '@/i18n/i18n';

export type MediaFileCategory = 'photo' | 'video' | 'audio';

export type MediaUploadAcceptKinds = 'images' | 'all';

/** Per-file size caps in bytes (all tiers). Mirrors API `MEDIA_FILE_SIZE_CAPS_BYTES`. */
export const MEDIA_FILE_SIZE_CAPS_BYTES: Readonly<Record<MediaFileCategory, number>> = {
  photo: 25 * 1024 * 1024,
  video: 500 * 1024 * 1024,
  audio: 100 * 1024 * 1024
};

/**
 * Soft-warn threshold for video (under the hard cap). Large phone recordings often
 * stall mid-upload or play poorly; guidance only — does not block.
 */
export const VIDEO_SOFT_WARN_BYTES = 100 * 1024 * 1024;

/** Common image types accepted for photos / profile / citations. */
export const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;

/** File input accept string for images only. */
export const IMAGE_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/*';

/** File input accept string for images, video, and audio. */
export const MEDIA_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,image/*,video/*,audio/*';

export function normalizeMimeType(mimeType: string | null | undefined): string {
  return (mimeType || '').split(';')[0].trim().toLowerCase();
}

export function getMediaFileCategoryFromMimeType(mimeType: string | null | undefined): MediaFileCategory | null {
  const normalized = normalizeMimeType(mimeType);
  if (!normalized) return null;
  if (normalized.startsWith('image/')) return 'photo';
  if (normalized.startsWith('video/')) return 'video';
  if (normalized.startsWith('audio/')) return 'audio';
  return null;
}

export function isPaidOnlyMediaCategory(category: MediaFileCategory): boolean {
  return category === 'video' || category === 'audio';
}

export function getMediaFileSizeCapBytes(category: MediaFileCategory): number {
  return MEDIA_FILE_SIZE_CAPS_BYTES[category];
}

export function formatBytesAsMb(bytes: number): number {
  return Math.round(bytes / (1024 * 1024));
}

export function getFileAcceptAttribute(acceptKinds: MediaUploadAcceptKinds): string {
  return acceptKinds === 'all' ? MEDIA_ACCEPT : IMAGE_ACCEPT;
}

export type MediaUploadValidationResult =
  | { ok: true; category: MediaFileCategory; softWarning?: string }
  | { ok: false; message: string; upgradeRequired?: boolean };

export function getVideoSizeGuidance(): string {
  return i18n.t('forms:upload.videoGuidance');
}

export function getVideoOversizeErrorMessage(): string {
  const maxMb = formatBytesAsMb(MEDIA_FILE_SIZE_CAPS_BYTES.video);
  return i18n.t('forms:upload.videoOversize', {
    maxMb,
    guidance: getVideoSizeGuidance()
  });
}

/** Non-blocking hint when a video is large but still under the hard cap. */
export function getVideoSoftWarning(fileSizeBytes: number): string | undefined {
  if (fileSizeBytes <= VIDEO_SOFT_WARN_BYTES) return undefined;
  if (fileSizeBytes > MEDIA_FILE_SIZE_CAPS_BYTES.video) return undefined;
  const sizeMb = formatBytesAsMb(fileSizeBytes);
  return i18n.t('forms:upload.videoSoftWarning', { sizeMb });
}

/**
 * Validate a file before calling the upload API.
 * When `acceptKinds` is `images`, only common image MIME types are allowed.
 * When `hasPaidPlan` is false, video/audio are rejected with upgrade messaging (API remains authoritative).
 */
export function validateMediaUploadFile(
  file: File,
  options: {
    acceptKinds?: MediaUploadAcceptKinds;
    hasPaidPlan?: boolean;
  } = {}
): MediaUploadValidationResult {
  const acceptKinds = options.acceptKinds ?? 'images';
  const mime = normalizeMimeType(file.type);
  const category = getMediaFileCategoryFromMimeType(mime);

  if (!category) {
    return {
      ok: false,
      message: acceptKinds === 'all' ? i18n.t('forms:upload.chooseMediaFile') : i18n.t('forms:upload.chooseImageFile')
    };
  }

  if (acceptKinds === 'images') {
    if (category !== 'photo' || !(ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(mime)) {
      return {
        ok: false,
        message: i18n.t('forms:upload.chooseImageFile')
      };
    }
  }

  if (isPaidOnlyMediaCategory(category) && options.hasPaidPlan === false) {
    return {
      ok: false,
      message: i18n.t('forms:upload.requiresFamilyPlan'),
      upgradeRequired: true
    };
  }

  const maxBytes = getMediaFileSizeCapBytes(category);
  if (!Number.isFinite(file.size) || file.size <= 0) {
    return { ok: false, message: i18n.t('forms:upload.fileSizeZero') };
  }
  if (file.size > maxBytes) {
    if (category === 'video') {
      return { ok: false, message: getVideoOversizeErrorMessage() };
    }
    const label = category === 'photo' ? i18n.t('forms:upload.imageLabel') : i18n.t('forms:upload.audioLabel');
    return {
      ok: false,
      message: i18n.t('forms:upload.sizeCap', { label, maxMb: formatBytesAsMb(maxBytes) })
    };
  }

  const softWarning = category === 'video' ? getVideoSoftWarning(file.size) : undefined;
  return softWarning ? { ok: true, category, softWarning } : { ok: true, category };
}

export function getUploadPromptHint(acceptKinds: MediaUploadAcceptKinds): string {
  if (acceptKinds === 'all') {
    return i18n.t('forms:upload.hintAll', {
      photoMb: formatBytesAsMb(MEDIA_FILE_SIZE_CAPS_BYTES.photo),
      videoMb: formatBytesAsMb(MEDIA_FILE_SIZE_CAPS_BYTES.video),
      audioMb: formatBytesAsMb(MEDIA_FILE_SIZE_CAPS_BYTES.audio)
    });
  }
  return i18n.t('forms:upload.hintImages', {
    photoMb: formatBytesAsMb(MEDIA_FILE_SIZE_CAPS_BYTES.photo)
  });
}

export function getUploadAriaLabel(acceptKinds: MediaUploadAcceptKinds): string {
  return acceptKinds === 'all' ? i18n.t('forms:upload.ariaMedia') : i18n.t('forms:upload.ariaImage');
}

/** Map API / MIME prefix filter values for search. */
export function mimeTypeFilterParam(filter: 'all' | 'images' | 'video' | 'audio'): string | undefined {
  if (filter === 'images') return 'image';
  if (filter === 'video') return 'video';
  if (filter === 'audio') return 'audio';
  return undefined;
}
